type LoadSoundBufferOptions = {
  getAudioContext: () => AudioContext;
  path: string;
};

type SoundBufferManager = {
  getBuffer: () => AudioBuffer | null;
  loadBuffer: () => Promise<AudioBuffer | null>;
  preload: (delayMs?: number) => void;
};

/**
 * Shared helper for lazily loading and preloading AudioBuffers.
 * Keeps sound modules small while preserving their current behavior.
 */
export function createSoundBufferManager({
  getAudioContext,
  path,
}: LoadSoundBufferOptions): SoundBufferManager {
  let buffer: AudioBuffer | null = null;
  let loading = false;

  async function loadBuffer(): Promise<AudioBuffer | null> {
    if (buffer) return buffer;
    if (loading) return null;
    loading = true;
    try {
      const ctx = getAudioContext();
      const res = await fetch(path);
      const arrayBuf = await res.arrayBuffer();
      buffer = await ctx.decodeAudioData(arrayBuf);
      return buffer;
    } catch {
      loading = false;
      return null;
    }
  }

  function preload(delayMs: number = 2000) {
    if (typeof window === "undefined") return;
    setTimeout(() => {
      try {
        void loadBuffer();
      } catch {
        // non-critical
      }
    }, delayMs);
  }

  return {
    getBuffer: () => buffer,
    loadBuffer,
    preload,
  };
}
